export type SiteNoticeVariant = 'error' | 'information' | 'default';

export interface SiteNotice {
  readonly id: 'technical-issues' | 'feature-announcement' | 'no-access';
  readonly message: string;
  readonly priority: number;
  readonly variant: SiteNoticeVariant;
}

type SiteNoticeEnvironment = Partial<
  Record<
    | 'VITE_SHOW_TECHNICAL_ISSUES_BANNER'
    | 'VITE_SHOW_FEATURE_ANNOUNCEMENT_BANNER'
    | 'VITE_FEATURE_ANNOUNCEMENT_BANNER_MESSAGE'
    | 'VITE_FEATURE_ANNOUNCEMENT_BANNER_EXPIRES_AT',
    string
  >
>;

const technicalIssuesBannerMessage =
  'BEV1 state-of-health (SOH) estimates may differ significantly from the real on-board SOH.';
const featureAnnouncementBannerMessage = 'New feature: Charging power stats (inside vehicle card)';
const noAccessBannerMessage =
  'You do not have access to Battery Monitoring. Contact support if you think this is incorrect.';

export const technicalIssuesSiteNotice: SiteNotice = {
  id: 'technical-issues',
  message: technicalIssuesBannerMessage,
  priority: 0,
  variant: 'default',
};

export const noAccessSiteNotice: SiteNotice = {
  id: 'no-access',
  message: noAccessBannerMessage,
  priority: 0,
  variant: 'error',
};

export const mergeSiteNotices = (siteNotices: readonly SiteNotice[]): readonly SiteNotice[] => {
  return [...new Map(siteNotices.map((siteNotice) => [siteNotice.id, siteNotice])).values()].toSorted(
    (left, right) => left.priority - right.priority,
  );
};

export const createSiteNotices = (
  environment: SiteNoticeEnvironment,
  currentDate: Date = new Date(),
): readonly SiteNotice[] => {
  const siteNotices: SiteNotice[] = [];

  if (environment.VITE_SHOW_TECHNICAL_ISSUES_BANNER === 'true') {
    siteNotices.push(technicalIssuesSiteNotice);
  }

  const featureAnnouncementBannerExpiresAt = environment.VITE_FEATURE_ANNOUNCEMENT_BANNER_EXPIRES_AT;
  const featureAnnouncementBannerHasNotExpired =
    featureAnnouncementBannerExpiresAt === undefined ||
    currentDate.getTime() < new Date(featureAnnouncementBannerExpiresAt).getTime();

  if (environment.VITE_SHOW_FEATURE_ANNOUNCEMENT_BANNER === 'true' && featureAnnouncementBannerHasNotExpired) {
    siteNotices.push({
      id: 'feature-announcement',
      message: environment.VITE_FEATURE_ANNOUNCEMENT_BANNER_MESSAGE ?? featureAnnouncementBannerMessage,
      priority: 1,
      variant: 'information',
    });
  }

  return mergeSiteNotices(siteNotices);
};

export const siteConfig = {
  title: import.meta.env.VITE_APP_TITLE as string,
  subTitle: import.meta.env.VITE_APP_SUB_TITLE as string,
  batteryMonitoringApi: import.meta.env.VITE_BATTERY_MONITORING_API as string,
  rowsPerPageDefault: 10,
  primaryColor: 'var(--tds-blue-500)',
  secondaryColor: 'var(--tds-blue-300)',
  tertiaryColor: 'var(--tds-blue-100)',
  siteNotices: createSiteNotices(import.meta.env as SiteNoticeEnvironment),
};
